import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';

import { warehouseApi } from 'api/warehouse';
import { UserContext } from 'contexts/UserContext';
import { Button } from 'shadcn/components/ui/Button';

import { BulkMovePalletsSummary } from './BulkMovePalletsSummary';

function BulkMovePalletsConfirmation({
    destinationLocationId,
    destinationLocationName,
    onCancel,
    onGoBack,
    onSuccess,
    selectedPalletIds,
}) {
    const { user } = useContext(UserContext);
    const numPallets = selectedPalletIds.length;
    const [isLoading, setIsLoading] = useState(false);
    const [moveResults, setMoveResults] = useState(null);
    const [error, setError] = useState(null);

    const handleConfirmMove = async () => {
        setIsLoading(true);
        setError(null); // Clear any previous errors
        try {
            const response = await warehouseApi.bulkMovePallets(selectedPalletIds, destinationLocationId);
            setMoveResults(response);
        } catch (error) {
            console.error('Failed to move pallets:', error);
            setError(error.message || 'Failed to move pallets. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        // Show success toast only if there were successful moves
        const numSuccesses = moveResults?.data?.successes ? Object.keys(moveResults.data.successes).length : 0;
        if (numSuccesses > 0) {
            onSuccess();
        } else {
            onCancel();
        }
    };

    const handleGoBack = () => {
        onGoBack();
    };

    if (moveResults) {
        return <BulkMovePalletsSummary moveResults={moveResults} onClose={handleClose} />;
    }

    return (
        <div className="p-6 flex flex-col items-center gap-6">
            <p className="text-lg text-center">
                Are you sure you want to move {numPallets} pallet{numPallets !== 1 ? 's' : ''} to location{' '}
                {destinationLocationName}?
            </p>
            {error && <p className="text-red-600 text-sm text-center">{error}</p>}
            <div className="flex gap-4">
                <Button variant="default" onClick={handleConfirmMove} disabled={isLoading}>
                    {isLoading ? (
                        <>
                            <svg
                                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                />
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                            </svg>
                            Moving Pallets...
                        </>
                    ) : (
                        'Yes, Move Pallets'
                    )}
                </Button>
                <Button variant="secondary" onClick={handleGoBack} disabled={isLoading}>
                    No, Go Back
                </Button>
            </div>
        </div>
    );
}

BulkMovePalletsConfirmation.propTypes = {
    destinationLocationId: PropTypes.string.isRequired,
    destinationLocationName: PropTypes.string.isRequired,
    onCancel: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    selectedPalletIds: PropTypes.arrayOf(PropTypes.number).isRequired,
};

export { BulkMovePalletsConfirmation };
